import AutosuggestHighlightMatch from 'autosuggest-highlight/match';
import AutosuggestHighlightParse from 'autosuggest-highlight/parse';
import { decode } from 'html-entities';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { useDebounce, useOnClickOutside, useSuggestions } from '../../hooks';
import { useStores } from '../../stores';
import {
  formatLocale,
  getStringLength,
  handleRegex,
  isIE,
  KEY_CODE_ARROW_DOWN,
  KEY_CODE_ARROW_UP,
  KEY_CODE_ENTER
} from '../../utilities';
import ButtonBase from '../ButtonBase';
import WarningIcon from '../Icons/Warning';
import InputBase from '../InputBase';
import ListBase from '../List';
import ListItemBase from '../ListItem';
import Scrollbar from '../Scrollbar';
import SvgIcon from '../SvgIcon';
import Timer from '../Timer';
import Uploader from '../Uploader';
import AttachmentButton from './AttachmentButton';
import SendButton from './SendButton';

const Recorder = React.lazy(() => import('./Recorder'));

/**
 * @todo
 *
 * - refactring
 * - decompositon
 * - move some parts to hook
 * - remove hardcoded values
 */
const MessageInput = () => {
  const { config, bot, chat, network, ui } = useStores();
  const {
    id,
    fontFamily,
    messageInput: {
      maxLength,
      borderRadius,
      placeholder,
      suggestionsCount,
      multiline,
      maxInputHeight,
      maxSuggestionsHeight,
      attachmentButton,
      recorder,
      sendButton,
      regexes,
      setGeneralSideSpace,
      onInit
    },
    window: { colors },
    scrollBar,
    overlapMenu
  } = config;
  const { i18n, t } = useTranslation();
  const scrollbar = React.useRef(null);
  const suggestionsRef = React.useRef([]);
  const [inputValue, setValue] = React.useState('');
  const debouncedSearchText = useDebounce(inputValue, 500);
  const inputLength = getStringLength(inputValue, false);
  const exceededInputLength = inputLength > maxLength;
  const [selection, setSelection] = React.useState({
    selecting: false,
    idx: undefined
  });
  const [isRecording, setRecording] = React.useState(false);
  const { suggestions, setSuggestions } = useSuggestions({
    ProjectId: id,
    SearchText: debouncedSearchText,
    SuggQuesCount: suggestionsCount,
    isDisabled: selection.selecting || isRecording
  });
  const [isOpenUploadModal, setOpenUploadModal] = React.useState(false);
  const [isShowInput, setShowInput] = React.useState(true);

  React.useEffect(() => {
    if (selection.selecting && !inputValue) {
      setSelection({ idx: undefined, selecting: false });
    }

    if (inputValue && ui.isOverlapMenuInputDisabled) setValue('');
  }, [inputValue, selection.selecting, ui.isOverlapMenuInputDisabled]);

  React.useEffect(() => {
    suggestionsRef.current = suggestionsRef.current.slice(0, suggestions.length);
  }, [suggestions.length]);

  React.useEffect(() => {
    ui.showExtraHeaderFilter(isOpenUploadModal);
  }, [isOpenUploadModal, ui]);

  React.useEffect(() => {
    if (chat.messages.length === 0 && onInit && onInit.enabled) {
      ui.setMessageInputDisabled(true);
      setShowInput(false);
    }
    if (onInit && onInit.enabled && !ui.isMessageInputDisabled) {
      setShowInput(true);
    }
  }, [chat.messages.length, onInit, ui]);

  const isEnabledRecorder = recorder && recorder.enabled && typeof recorder.ws === 'string';
  const hasMessages = chat.messages && chat.messages.length > 0;
  const hasSuggestions = suggestions && suggestions.length > 0;
  const hasNetworkProblems = !network.isOnline || network.isSlowConnection;
  const canSubmit =
    !isRecording && !exceededInputLength && !hasNetworkProblems && inputValue !== '';

  const handleSubmit = React.useCallback(
    (e, value) => {
      e.preventDefault();

      if (!canSubmit) {
        return;
      }

      // Reset suggestions.
      setSuggestions([]);

      // Reset selection.
      setSelection({ idx: undefined, selecting: false });
      // text for user
      const currentValue =
        regexes && regexes.enabled && regexes.items.length > 0
          ? handleRegex(regexes.items, value)
          : value;

      // text for API-request
      const questionText =
        regexes && regexes.enabled && regexes.sendOrgQuestion ? value : currentValue;

      bot.askQuestion({
        Question: questionText.trim(),
        UserText: currentValue.trim(),
        TextLanguage: formatLocale(i18n.language)
      });

      // Reset input.
      setValue('');

      if (overlapMenu && overlapMenu.enabled && overlapMenu.options.messageInputEnabled) {
        ui.startChatWithoutCategory();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bot, canSubmit, i18n.language, overlapMenu, setSuggestions, ui]
  );

  const handleArrowUp = React.useCallback(
    e => {
      if (!hasSuggestions) return;

      e.preventDefault();
      e.stopPropagation();

      const idx =
        typeof suggestions[selection.idx - 1] === 'undefined'
          ? suggestions.length - 1
          : selection.idx - 1;

      setSelection({ idx, selecting: true });

      if (suggestions[idx] && suggestions[idx].MasterQuestion) {
        setValue(suggestions[idx].MasterQuestion);
      }

      if (scrollbar.current && suggestionsRef.current && suggestionsRef.current[idx]) {
        scrollbar.current.scrollTop(suggestionsRef.current[idx].offsetTop);
      }
    },
    [hasSuggestions, selection.idx, suggestions]
  );

  const handleArrowDown = React.useCallback(
    e => {
      if (!hasSuggestions) return;

      e.preventDefault();
      e.stopPropagation();

      const idx = typeof suggestions[selection.idx + 1] === 'undefined' ? 0 : selection.idx + 1;

      setSelection({ idx, selecting: true });

      if (suggestions[idx] && suggestions[idx].MasterQuestion) {
        setValue(suggestions[idx].MasterQuestion);
      }

      if (scrollbar.current && suggestionsRef.current && suggestionsRef.current[idx]) {
        scrollbar.current.scrollTop(suggestionsRef.current[idx].offsetTop);
      }
    },
    [hasSuggestions, selection.idx, suggestions]
  );

  const handleKeyDown = React.useCallback(
    e => {
      switch (e.keyCode) {
        case KEY_CODE_ENTER:
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e, inputValue);
          break;
        case KEY_CODE_ARROW_UP:
          handleArrowUp(e);
          break;
        case KEY_CODE_ARROW_DOWN:
          handleArrowDown(e);
          break;
        default:
          break;
      }
    },
    [handleArrowDown, handleArrowUp, handleSubmit, inputValue]
  );

  if (!hasMessages && hasNetworkProblems) {
    return null;
  }

  const ref = React.useRef();
  useOnClickOutside(ref, () => setSuggestions([]));

  const formMarginSide = setGeneralSideSpace ? '20px' : '10px';

  return (
    <RootForm
      textFontFamily={fontFamily}
      marginSides={formMarginSide}
      borderRadius={borderRadius}
      isMobile={ui.isMobile}
      ref={ref}
    >
      {isRecording && <Timer />}
      <Wrapper borderRadius={borderRadius} isMobile={ui.isMobile}>
        {exceededInputLength && (
          <Alert
            textColor={colors.main}
            backgroundColor={colors.warning}
            borderRadius={borderRadius}
          >
            <Icon fill={colors.main}>
              <WarningIcon />
            </Icon>
            {t('longMessage')} ({t('moreOn')} &quot;{inputLength - maxLength}&quot;{' '}
            {t('characters')})
          </Alert>
        )}
        {hasSuggestions && (
          <List>
            <Scrollbar
              ref={scrollbar}
              autoHeight
              autoHeightMax={maxSuggestionsHeight}
              {...scrollBar}
            >
              {suggestions.map(({ MasterQuestion }, i) => {
                const matches = AutosuggestHighlightMatch(MasterQuestion, debouncedSearchText);

                const parts = AutosuggestHighlightParse(MasterQuestion, matches);

                return (
                  <ListItem
                    key={i}
                    active={i === selection.idx}
                    ref={el => {
                      suggestionsRef.current[i] = el;
                    }}
                  >
                    <ListButton type="button" onClick={e => handleSubmit(e, MasterQuestion)}>
                      {parts.map(({ highlight, text }, j) => (
                        <Highlight active={highlight} key={j}>
                          {decode(text)}
                        </Highlight>
                      ))}
                    </ListButton>
                  </ListItem>
                );
              })}
            </Scrollbar>
            <Line />
          </List>
        )}
        <InputWrapper>
          {isShowInput ? (
            <Input
              autoComplete="on"
              multiline={multiline}
              async
              maxHeight={maxInputHeight}
              value={inputValue}
              autoFocus={false}
              disabled={
                hasNetworkProblems || ui.isOverlapMenuInputDisabled || ui.isMessageInputDisabled
              }
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              borderRadius={borderRadius}
              isMobile={ui.isMobile}
              placeholder={hasNetworkProblems ? t('connectError') : t(placeholder)}
              renderSuffix={
                <>
                  {!isIE() && isEnabledRecorder && (
                    <Recorder
                      webSocketUrl={recorder.ws}
                      onStart={() => setRecording(true)}
                      onStop={() => setRecording(false)}
                      onUpdate={value => setValue(value)}
                      isDisabled={hasNetworkProblems}
                      recordButtonProps={recorder.recordButton}
                    />
                  )}
                  <>
                    {attachmentButton.enabled && (
                      <AttachmentButton
                        icon={attachmentButton.icon}
                        hoverBackground={attachmentButton.hoverBackground}
                        background={attachmentButton.background}
                        borderRadius={attachmentButton.borderRadius}
                        onClick={() => setOpenUploadModal(true)}
                        disabled={hasNetworkProblems || isRecording}
                      />
                    )}
                    {sendButton && (
                      <SendButton
                        variant={sendButton.variant}
                        label={sendButton.label}
                        icon={sendButton.icon}
                        color={sendButton.color}
                        hoverBackground={sendButton.hoverBackground}
                        background={sendButton.background}
                        borderRadius={sendButton.borderRadius}
                        onClick={e => handleSubmit(e, inputValue)}
                        disabled={!canSubmit || isRecording}
                      />
                    )}
                  </>
                </>
              }
            />
          ) : null}
        </InputWrapper>
      </Wrapper>
      {isOpenUploadModal && (
        <Uploader
          onClose={() => setOpenUploadModal(!isOpenUploadModal)}
          isLoadingBar
          notification={false}
        />
      )}
    </RootForm>
  );
};

const RootForm = styled.form`
  position: relative;
  z-index: 2;
  width: auto;
  height: auto;
  padding: ${({ borderRadius }) => (borderRadius === 0 ? '6px 0 0' : '6px 0')};
  padding-bottom: ${props => props.isMobile && 0};
  margin-top: -2px;
  margin-bottom: 0;
  margin-right: ${({ borderRadius, isMobile, marginSides }) =>
    isMobile || borderRadius === 0 ? 0 : marginSides};
  margin-left: ${({ borderRadius, isMobile, marginSides }) =>
    isMobile || borderRadius === 0 ? 0 : marginSides};
  font-family: ${({ textFontFamily }) => textFontFamily};
`;

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  background-color: #fff;
  border-radius: ${({ borderRadius }) => `${borderRadius}px`};
  overflow: hidden;

  ${({ isMobile }) => {
    if (isMobile) {
      return css`
        border-radius: 0;
      `;
    }

    return null;
  }}
`;

const Line = styled.div`
  display: block;
  height: 1px;
  margin-left: 30px;
  margin-right: 30px;
  background-color: #c4d1d8;
`;

const List = styled(ListBase)`
  padding: 2px;
`;

const ListItem = styled(ListItemBase)`
  display: block;
  padding-left: 30px;
  padding-right: 30px;
  border-bottom: none;

  // :first-of-type {
  //   padding-top: 10px;
  // }

  background-color: ${props => props.active && '#f4f4f4'};

  :hover {
    background-color: #f4f4f4;
  }
`;

const ListButton = styled(ButtonBase)`
  display: block;
  text-align: left;

  :hover {
    color: #144271;
  }
`;

const InputWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    margin-bottom: -8px;
  }
`;

const Input = styled(InputBase)`
  z-index: 1;
  box-sizing: border-box;
  width: 100%;
  background-color: #fff;
  border: none;
  border-radius: ${({ borderRadius, isMobile }) => (isMobile ? 0 : `${borderRadius}px`)};
  outline: none;
  & label {
    padding-right: 8px;
    font-size: 16px;
  }
`;

const Highlight = styled.span`
  ${({ active }) =>
    active &&
    css`
      font-weight: 700;
      color: #00416f;
      background-color: #e5ecf0;
    `}
`;

const Alert = styled.div`
  position: absolute;
  top: -2px;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 30px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  color: ${props => props.textColor};
  background-color: ${props => props.backgroundColor};
  border-radius: ${({ borderRadius }) => `${borderRadius}px ${borderRadius}px 0 0`};
`;

const Icon = styled.i`
  display: flex;
  margin-right: 2px;

  ${SvgIcon} {
    width: 12px;
    height: 12px;
    fill: ${props => props.fill};
  }
`;

export default observer(MessageInput);
